import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sparkles, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AIAnalysisPanel({ issues, context, token, isOpen, onClose }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAnalyze = async () => {
    if (!issues || issues.length === 0) {
      toast.error('Nessuna issue da analizzare');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ai/analyze`,
        {
          issues: issues.slice(0, 50),
          context,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 300000,
        }
      );
      setAnalysis(response.data.analysis);
      toast.success(`Analisi completata su ${response.data.issues_analyzed} issue`);
    } catch (error) {
      console.error('AI Analysis error:', error);
      
      // Extract error message from response
      let errorMessage = 'Errore nell\'analisi AI';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.status === 503) {
        errorMessage = 'Il servizio Gemini AI è temporaneamente sovraccarico. Riprova tra qualche minuto.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Quota API esaurita. Riprova più tardi.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        duration: 6000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed right-0 top-0 h-full w-[600px] bg-[rgb(24,24,27)] border-l border-[rgb(39,39,42)] shadow-2xl z-50 transform transition-transform duration-300 ease-out"
      data-testid="ai-analysis-panel"
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-6 border-b border-[rgb(39,39,42)] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="h-6 w-6 text-[rgb(59,130,246)]" />
            <div>
              <h2 className="text-xl font-bold font-mono">Analisi AI</h2>
              <p className="text-xs text-[rgb(161,161,170)] font-mono uppercase tracking-widest mt-1">
                Powered by Gemini 2.5 Flash
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="close-ai-panel"
            className="hover:bg-white/5"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {!analysis && (
            <div className="p-6 flex-1 flex flex-col items-center justify-center text-center">
              <Sparkles className="h-16 w-16 text-[rgb(59,130,246)] mb-4" />
              <h3 className="text-lg font-mono font-bold mb-2">Analizza con AI</h3>
              <p className="text-[rgb(161,161,170)] text-sm mb-6 max-w-md">
                L'AI analizzerà le issue presenti in questa vista e fornirà insights su pattern,
                tempi di risoluzione e aree critiche.
              </p>
              <Button
                onClick={handleAnalyze}
                disabled={loading}
                className="bg-[rgb(59,130,246)] hover:bg-[rgb(59,130,246)]/90 font-mono glow-primary"
                data-testid="start-ai-analysis"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analisi in corso...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Avvia Analisi
                  </>
                )}
              </Button>
              <p className="text-xs text-[rgb(161,161,170)] mt-4">
                {issues?.length || 0} issue disponibili per l'analisi
              </p>
            </div>
          )}

          {analysis && (
            <>
              <ScrollArea className="flex-1 p-6">
                <div className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap" data-testid="ai-analysis-content">
                  {analysis}
                </div>
              </ScrollArea>
              <div className="p-4 border-t border-[rgb(39,39,42)] flex gap-2">
                <Button
                  onClick={() => setAnalysis(null)}
                  variant="outline"
                  className="flex-1 border-[rgb(39,39,42)] hover:bg-white/5 font-mono"
                  data-testid="clear-analysis"
                >
                  Nuova Analisi
                </Button>
                <Button
                  onClick={handleAnalyze}
                  disabled={loading}
                  className="flex-1 bg-[rgb(59,130,246)] hover:bg-[rgb(59,130,246)]/90 font-mono"
                  data-testid="refresh-analysis"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  Aggiorna
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AIAnalysisPanel;